/* ============================================================
   ShoLi — View Manager Module
   Manages view stack, navigation, and header updates.
   ============================================================ */

export type ViewName = "checking" | "edit" | "data" | "settings";

export interface ViewManagerApi {
  init: () => void;
  showView: (viewName: ViewName) => void;
  getCurrentView: () => ViewName;
  goBack: () => void;
}

interface ViewManagerConfig {
  renderFn?: () => void;
  setSearchInputValue?: (val: string) => void;
}

export function createViewManager(config: ViewManagerConfig = {}): ViewManagerApi {
  const { renderFn, setSearchInputValue } = config;

  let currentView: ViewName = "checking";
  const viewStack: ViewName[] = ["checking"];
  const homeView: ViewName = "checking";

  function init(): void {
    document.getElementById("view-checking")?.classList.add("active");
    const titleEl = document.getElementById("app-title");
    if (titleEl) titleEl.textContent = "ShoLi";
    updateHeader();
  }

  function updateHeader(): void {
    const btnBack = document.getElementById("btn-back");
    const btnViewSwitch = document.getElementById("btn-view-switch");
    const btnMenu = document.getElementById("btn-menu");
    const headerActions = document.querySelector(".header-actions");

    if (currentView === homeView) {
      btnBack?.classList.add("hidden");
      headerActions?.classList.remove("hidden");
      btnViewSwitch?.setAttribute("innerHTML", "&#9998;");
      btnViewSwitch?.setAttribute("title", "Edit mode");
      btnMenu?.classList.remove("hidden");
    } else if (currentView === "edit") {
      btnBack?.classList.remove("hidden");
      headerActions?.classList.remove("hidden");
      btnViewSwitch?.setAttribute("innerHTML", "&#9776;");
      btnViewSwitch?.setAttribute("title", "Menu");
      btnMenu?.classList.add("hidden");
    } else {
      btnBack?.classList.remove("hidden");
      headerActions?.classList.add("hidden");
    }
  }

  function _switchView(viewName: ViewName): void {
    const views: ViewName[] = ["checking", "edit", "data", "settings"];
    views.forEach((v) => {
      const el = document.getElementById(`view-${v}`);
      if (el) el.classList.toggle("active", v === viewName);
    });
    currentView = viewName;

    const titles: Record<ViewName, string> = {
      checking: "ShoLi",
      edit: "Edit Items",
      data: "Data",
      settings: "Settings",
    };
    const titleEl = document.getElementById("app-title");
    if (titleEl) titleEl.textContent = titles[viewName];

    updateHeader();
    document.getElementById("menu-overlay")?.classList.add("hidden");

    renderFn?.();

    if (viewName === "edit") {
      setSearchInputValue?.("");
    }

    if (viewName === "data") {
      document.getElementById("data-export-area")?.classList.add("hidden");
      document.getElementById("data-import-area")?.classList.add("hidden");
    }
  }

  function showView(viewName: ViewName): void {
    viewStack.push(currentView);
    _switchView(viewName);
  }

  function getCurrentView(): ViewName {
    return currentView;
  }

  function goBack(): void {
    if (viewStack.length > 1) {
      const target = viewStack.pop();
      if (target) _switchView(target);
    }
  }

  return { init, showView, getCurrentView, goBack };
}
